import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { logger } from '../stores';

let ffmpeg = null;
let currentLogSubscriber = null;
let currentProgressSubscriber = null;

export async function getFFmpeg() {
    if (ffmpeg) {
        return ffmpeg;
    }

    ffmpeg = new FFmpeg();

    // Log ffmpeg output to console
    ffmpeg.on('log', ({ message }) => {
        logger.log(`[ffmpeg] ${message}`);
        if (currentLogSubscriber) currentLogSubscriber(message);
    });

    ffmpeg.on('progress', ({ progress, time }) => {
        if (currentProgressSubscriber) currentProgressSubscriber(progress);
    });

    // Use new URL to ensure absolute path, so the Web Worker resolves it correctly
    // Since index.html is in the root, and the assets are in bundle/ffmpeg
    const baseURL = new URL('bundle/ffmpeg', window.location.origin + window.location.pathname).href;

    try {
        // Load ffmpeg-core 
        // single-threaded version is used since SharedArrayBuffer is not guaranteed in all environments yet,
        // though the server will send correct headers for the app itself if needed.
        await ffmpeg.load({
            coreURL: `${baseURL}/ffmpeg-core.js`,
            wasmURL: `${baseURL}/ffmpeg-core.wasm`,
            classWorkerURL: `${baseURL}/worker.js`
        });

        logger.log('FFmpeg loaded successfully');
        return ffmpeg;
    } catch (err) {
        logger.error(err, 'Failed to load FFmpeg');
        ffmpeg = null;
        throw err;
    }
}

export function needsTranscoding(blob) {
    // If the mime type explicitly contains webm, it's a webm.
    // Sometimes android spits out video/webm;codecs=vp8 and iOS cannot play this.
    return blob.type.includes('video/webm');
}

export async function transcodeToMp4(webmBlob, options = {}) {
    logger.log('Starting transcoding process for ' + webmBlob.type);

    currentLogSubscriber = options.onLog || null;
    currentProgressSubscriber = options.onProgress || null;

    try {
        const ffmpegInstance = await getFFmpeg();

        const inputName = 'input.webm';
        const outputName = 'output.mp4';

        // Write the file to ffmpeg's virtual file system
        await ffmpegInstance.writeFile(inputName, await fetchFile(webmBlob));

        // Run the ffmpeg command to convert webm to mp4
        // -c:v libx264: encode using h264
        // -preset superfast: faster encoding, acceptable for mobile
        // -crf 28: smaller file size
        // -vf scale...: ensure even dimensions (libx264 requirement)
        // -c:a aac: encode audio to AAC
        await ffmpegInstance.exec([
            '-i', inputName,
            '-c:v', 'libx264',
            '-pix_fmt', 'yuv420p',
            '-preset', 'superfast',
            '-crf', '28',
            '-vf', 'fps=30,scale=trunc(iw/2)*2:trunc(ih/2)*2',
            '-c:a', 'aac',
            outputName
        ]);

        // Read the resulting file
        const data = await ffmpegInstance.readFile(outputName);

        // Clean up the virtual file system
        await ffmpegInstance.deleteFile(inputName);
        await ffmpegInstance.deleteFile(outputName);

        // Create a new blob from the output data
        const mp4Blob = new Blob([data.buffer], { type: 'video/mp4' });
        logger.log('Transcoding finished, produced MP4 blob of size ' + mp4Blob.size);

        return mp4Blob;
    } finally {
        currentLogSubscriber = null;
        currentProgressSubscriber = null;
    }
}
